import { Checkbox, Segment, SegmentGroup } from 'semantic-ui-react';
import { Link } from 'react-router';
import styled from '@emotion/styled';
import { Button, SuiTable } from '@britishcouncil/react-admin';
import { usePermissions } from '@britishcouncil/react-common';

import { EorSpeakingMonitoringGroup, Permission } from 'api';
import { useExaminerId } from 'components';
import { useTableState } from 'hooks';
import { useEorSpeakingMonitoringListQuery } from 'store';
import { TableState } from 'types';
import { columns } from './EorSpeakingMonitoringList.columns';

export const EorSpeakingMonitoringList = () => {
  const examinerId = useExaminerId();
  const { permissions } = usePermissions();
  const { tableState, setTableState, setFilterState } = useTableState(INITIAL_TABLE_STATE);
  const { filters, ...pagedTableState } = tableState;

  const { data, isFetching } = useEorSpeakingMonitoringListQuery({
    examinerId,
    pageIndex: pagedTableState.pageIndex,
    pageSize: pagedTableState.pageSize,
    showDeleted: filters.showDeleted,
  });

  const showAddButton = permissions.includes(Permission.UpdateEorMonitoringResults);

  return (
    <SegmentGroup>
      <S.Segment>
        <S.ActionsSegment>
          <Checkbox
            fitted
            label="Show deleted"
            checked={filters.showDeleted}
            onChange={(_, { checked }) => setFilterState({ showDeleted: checked })}
          />
          {showAddButton && (
            <Button primary as={Link} to="add" icon="plus" content="Add monitoring" />
          )}
        </S.ActionsSegment>
      </S.Segment>
      <SuiTable
        disableFilters
        disableSortBy
        data={data?.results ?? []}
        state={pagedTableState}
        onStateChange={setTableState}
        loading={isFetching}
        totalCount={data?.count}
        columns={columns}
        tableProps={{ 'data-testid': 'eor-monitoring-list-table' }}
        tableRowProps={(_, { row }) => ({
          negative: row.original.isDeleted,
          className: row.original.isDeleted ? 'deleted-record' : '',
        })}
      />
    </SegmentGroup>
  );
};

const INITIAL_TABLE_STATE: TableState<EorSpeakingMonitoringGroup, { showDeleted?: boolean }> = {
  pageIndex: 0,
  pageSize: 10,
  sortBy: [],
  filters: { showDeleted: false },
};

const S = {
  Segment: styled(Segment)`
    && {
      display: flex;
    }
  `,
  ActionsSegment: styled.div`
    display: flex;
    justify-content: end;
    align-items: center;
    gap: 1em;
    flex: 1;
  `,
};
